import React, { useState } from 'react';
import { X, Users, MapPin, Pin } from 'lucide-react';
import { StaffMessage, Employee, Position, Schedule } from '../../types';
import { fixUtf8Mojibake } from '../../utils/fixUtf8Mojibake';
import { getProfileImageUrl } from '../../services/api';

interface ThreadInfoDrawerProps {
  thread: StaffMessage;
  employees: Employee[];
  positions: Position[];
  schedules: Schedule[];
  onClose: () => void;
  onPinnedNoteSave: (note: string | null) => Promise<void>;
  canEditPinnedNote: boolean;
}

const ThreadInfoDrawer: React.FC<ThreadInfoDrawerProps> = ({
  thread,
  employees,
  positions,
  schedules,
  onClose,
  onPinnedNoteSave,
  canEditPinnedNote,
}) => {
  const [pinnedNote, setPinnedNote] = useState(thread.pinnedNote ?? thread.description ?? '');
  const [saving, setSaving] = useState(false);

  const handleSavePin = async () => {
    try {
      setSaving(true);
      await onPinnedNoteSave(pinnedNote.trim() || null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white shadow-xl h-full overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-neutral-200">
          <h2 className="text-lg font-bold text-neutral-900">Chat info</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-4 space-y-6">
          <div>
            <h3 className="text-sm font-semibold text-neutral-700 mb-1">
              {fixUtf8Mojibake(thread.displayName ?? thread.title)}
            </h3>
            {thread.location?.name && (
              <p className="text-xs text-neutral-500 flex items-center gap-1">
                <MapPin className="h-3 w-3" /> {thread.location.name}
              </p>
            )}
            <p className="text-xs text-neutral-500 mt-1 capitalize">{thread.threadType ?? 'group'} chat</p>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-neutral-700 mb-2 flex items-center gap-1">
              <Pin className="h-4 w-4" /> Pinned note
            </h3>
            {canEditPinnedNote ? (
              <>
                <textarea
                  value={pinnedNote}
                  onChange={(e) => setPinnedNote(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
                  placeholder="Optional note pinned at top of chat..."
                />
                <button
                  onClick={handleSavePin}
                  disabled={saving}
                  className="mt-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save pinned note'}
                </button>
              </>
            ) : (
              <p className="text-sm text-neutral-600">
                {fixUtf8Mojibake(thread.pinnedNote ?? thread.description) || 'No pinned note'}
              </p>
            )}
          </div>

          <div>
            <h3 className="text-sm font-semibold text-neutral-700 mb-2 flex items-center gap-1">
              <Users className="h-4 w-4" /> Members ({thread.participants?.length ?? 0})
            </h3>
            <ul className="space-y-2 max-h-48 overflow-y-auto">
              {thread.participants?.map((p) => {
                const url = getProfileImageUrl(p.profileImageUrl ?? employees.find((e) => e.id === p.id)?.profileImageUrl);
                return (
                  <li key={p.id} className="flex items-center gap-2 text-sm">
                    {url ? (
                      <img src={url} alt="" className="h-8 w-8 rounded-full object-cover" />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-semibold">
                        {p.firstName?.[0]}{p.lastName?.[0]}
                      </div>
                    )}
                    <span>{p.firstName} {p.lastName}</span>
                  </li>
                );
              })}
            </ul>
          </div>

          {(thread.assignedToPositions?.length ?? 0) > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-neutral-700 mb-2">Smart group — Positions</h3>
              <ul className="text-sm text-neutral-600 space-y-1">
                {thread.assignedToPositions?.map((p) => (
                  <li key={p.id}>• {p.title}</li>
                ))}
              </ul>
              <p className="text-xs text-neutral-500 mt-1">All staff on these positions at this location can access full history.</p>
            </div>
          )}

          {(thread.assignedToSchedules?.length ?? 0) > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-neutral-700 mb-2">Smart group — Schedules</h3>
              <ul className="text-sm text-neutral-600 space-y-1">
                {thread.assignedToSchedules?.map((s) => (
                  <li key={s.id}>• {s.name}</li>
                ))}
              </ul>
              <p className="text-xs text-neutral-500 mt-1">All staff on these schedules at this location can access full history.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ThreadInfoDrawer;
